import { useMemo, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { StationDetails, type StationDetailsProps } from '@metro/station-details'
import type { MetroAnalyticsReporter } from '../interaction/metroAnalyticsReporter'
import { buildStationDetailsCopy } from '../i18n/packageStrings'
import { useStationDetailsAnalytics } from './useStationDetailsAnalytics'

/**
 * Host wrapper around `<StationDetails>` that wires the §8 analytics
 * facade through {@link useStationDetailsAnalytics}. Lives in the
 * host (`src/components/`) so the package stays stateless +
 * domain-agnostic (`feedback_reusable_module_domain_agnostic`).
 *
 * Use this wrapper at every site where the host mounts the V2 details
 * component — the modal phone mount (`AppShell.tsx`) and the expanded
 * sidebar mount (`StationCardOrDetailsRouter.renderStationDetails`).
 * Both call sites used to pass raw `on*` callbacks; routing through
 * this wrapper guarantees the 6 §8 events emit at runtime.
 */
export interface StationDetailsWithAnalyticsProps extends StationDetailsProps {
  readonly reporter: MetroAnalyticsReporter
}

export function StationDetailsWithAnalytics({
  reporter,
  ...rest
}: StationDetailsWithAnalyticsProps): ReactElement {
  // `hasStation` defaults to true in the package — analytics should only
  // fire `_opened` when the host actually has a resolved station. We
  // mirror the package's default so the gate stays in lockstep without
  // re-importing the prop type's default machinery.
  const hasStation = rest.hasStation ?? true
  const { t } = useTranslation('station-details')
  // Locale-aware copy for the domain-agnostic package (host injection).
  // A caller-supplied `copy` (e.g. a test/visual fixture) wins.
  const copy = useMemo(() => buildStationDetailsCopy(t), [t])
  const wiring = useStationDetailsAnalytics({
    stationId: hasStation ? rest.stationId : null,
    reporter,
    onFavoriteTap: rest.onFavoriteTap,
    onTransferTap: rest.onTransferTap,
    onPaywallTap: rest.onPaywallTap,
    onReportIssueTap: rest.onReportIssueTap,
    isFavorite: rest.isFavorite,
    isPremium: rest.isPremium,
  })

  return (
    <StationDetails
      copy={copy}
      {...rest}
      onFavoriteTap={wiring.onFavoriteTap}
      onTransferTap={wiring.onTransferTap}
      onPaywallTap={wiring.onPaywallTap}
      onReportIssueTap={wiring.onReportIssueTap}
    />
  )
}
